import { TFunction } from 'i18next';
import { CombinedGraphQLErrors } from '@apollo/client';
import { ServerError } from '@apollo/client/errors';
import { GraphQLErrorCode } from '@insio/validation';

/** True when the backend demands a CAPTCHA for this sign-in (escalation ladder). */
export function isCaptchaRequiredError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return errorMessage.includes('CAPTCHA_REQUIRED');
}

/**
 * Maps backend error messages to translated error messages
 */
/** Reads `extensions.errorCode` off an Apollo error, when the backend supplied one. */
function extractErrorCode(error: Error): string | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;
  return error.errors[0]?.extensions?.['errorCode'] as string | undefined;
}

export function translateAuthError(error: Error | string, t: TFunction): string {
  const errorMessage = typeof error === 'string' ? error : error.message;
  // A structured errorCode is authoritative — matching on prose only works while the
  // backend keeps its wording, and the OAuth BFF hands us a bare code in ?error=.
  const errorCode = typeof error === 'string' ? error : extractErrorCode(error);

  // The global request limit rejects at the HTTP layer, so it arrives as a ServerError
  if (ServerError.is(error) && error.statusCode === 429) {
    return t('features.auth.errors.signupRateLimited');
  }

  // Check for rate limit error first (needs special handling for extracting seconds and converting to minutes)
  const rateLimitMatch = errorMessage.match(
    /too many.*login attempts.*try again in (\d+) seconds/i
  );
  if (rateLimitMatch) {
    const seconds = parseInt(rateLimitMatch[1], 10);
    const minutes = Math.ceil(seconds / 60); // Convert to minutes, round up
    return t('features.auth.errors.rateLimitExceeded', { minutes });
  }

  // Common backend error patterns
  const errorMappings: Record<string, string> = {
    CAPTCHA_REQUIRED: 'features.auth.errors.captchaRequired',
    CAPTCHA_FAILED: 'features.auth.errors.captchaFailed',
    MAGIC_LINK_EMAIL_SEND_FAILED: 'features.auth.errors.magicLinkSendFailed',
    [GraphQLErrorCode.RATE_LIMIT_EXCEEDED]: 'features.auth.errors.signupRateLimited',
    DISPOSABLE_EMAIL_NOT_ALLOWED: 'features.auth.errors.disposableEmail',
    EMAIL_ALREADY_REGISTERED: 'features.auth.errors.emailAlreadyExists',
    ACCOUNT_NAME_TAKEN: 'features.auth.errors.accountNameTaken',
    NO_ACTIVE_MEMBERSHIP: 'features.auth.errors.noActiveMembership',
    OAUTH_EMAIL_NOT_VERIFIED: 'features.auth.oauth.emailNotVerified',
    AUTH_CANCELLED: 'features.auth.oauth.cancelled',
    AUTH_FAILED: 'features.auth.oauth.error',
    'This organization name is already taken': 'features.auth.errors.accountNameTaken',
    'Invalid email or password': 'features.auth.errors.invalidCredentials',
    'Invalid credentials': 'features.auth.errors.invalidCredentials',
    'Email is already registered': 'features.auth.errors.emailAlreadyExists',
    'Email already exists': 'features.auth.errors.emailAlreadyExists',
    'User not found': 'features.auth.errors.emailNotFound',
    'Email not found': 'features.auth.errors.emailNotFound',
    'No account found with this email': 'features.auth.errors.emailNotFound',
    'Invalid email format': 'features.auth.errors.invalidEmail',
    'Password is required': 'features.auth.errors.passwordRequired',
    'Password must be at least 8 characters': 'features.auth.errors.passwordTooShort',
    'Password too short': 'features.auth.errors.passwordTooShort',
    'Password must contain uppercase, lowercase, and number':
      'features.auth.errors.passwordTooWeak',
    'Weak password': 'features.auth.errors.passwordTooWeak',
    'Account name is required': 'features.auth.errors.accountNameRequired',
    'First name is required': 'features.auth.errors.firstNameRequired',
    'Last name is required': 'features.auth.errors.lastNameRequired',
    'Network error': 'features.auth.errors.networkError',
    'Failed to fetch': 'features.auth.errors.networkError',
    'Session expired': 'features.auth.errors.sessionExpired',
    'Invalid token': 'features.auth.errors.invalidToken',
    'Token expired': 'features.auth.errors.invalidToken',
    'Verification failed': 'features.auth.errors.verificationFailed',
    'Your organization has been deactivated': 'features.auth.errors.accountDeactivated'
  };

  if (errorCode && errorMappings[errorCode]) {
    return t(errorMappings[errorCode]);
  }

  // Try exact match first
  if (errorMappings[errorMessage]) {
    return t(errorMappings[errorMessage]);
  }

  // Try partial match for error messages that contain known patterns
  for (const [pattern, translationKey] of Object.entries(errorMappings)) {
    if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return t(translationKey);
    }
  }

  // Check for GraphQL validation errors
  if (errorMessage.includes('validation') || errorMessage.includes('Validation')) {
    return t('features.auth.errors.unknownError');
  }

  // Return translated unknown error for unrecognized errors
  return t('features.auth.errors.unknownError');
}

/**
 * Error type for better error categorization
 */
export type AuthErrorType = 'network' | 'validation' | 'authentication' | 'unknown';

/**
 * Categorizes error type for better UI feedback
 */
export function categorizeAuthError(error: Error | string): AuthErrorType {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('connection')
  ) {
    return 'network';
  }

  if (
    lowerMessage.includes('validation') ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('required')
  ) {
    return 'validation';
  }

  if (
    lowerMessage.includes('credentials') ||
    lowerMessage.includes('password') ||
    lowerMessage.includes('unauthorized')
  ) {
    return 'authentication';
  }

  return 'unknown';
}
